/**
 * Per-key circuit breaker for the TanStack Query layer.
 *
 * TanStack Query has no built-in circuit breaker. `useCache` opens one per hook
 * instance to stop hammering the backend during outages; this module ports that
 * behavior to a keyed, subscribable store so query hooks can gate on it
 * (`enabled: !isOpen(key)`) in a later migration phase (epic #2266).
 *
 * Trip conditions (same as useCache):
 *  - auth (401/403) or rate-limit (429) → open immediately
 *  - gateway (502/504) after ≥2 consecutive failures
 *  - any type after ≥3 consecutive failures
 *
 * Auto-reset (half-open) with exponential backoff (30s, 60s, 120s cap) for any
 * NON-auth trip; a success resets immediately. This intentionally broadens
 * useCache's "retryable only" auto-reset: useCache relied on a manual
 * force-refetch to recover from rate-limit/non-retryable trips, but this store
 * is designed for `enabled: !isOpen(key)` gating where there is no manual
 * escape hatch — so a 429 must self-heal on a timer or the query sticks forever.
 * Auth is deliberately excluded: it needs a real success (token refresh / login).
 */

import { classifyError } from './errorClassification.js';

const AUTO_RESET_BASE_MS = 30000;
const AUTO_RESET_CAP_MS = 120000;

/**
 * Create an isolated circuit-breaker store (also used as the app singleton).
 * Isolation makes it trivially testable without shared global state.
 */
export function createCircuitBreaker() {
  /**
   * @type {Map<string, {
   *   isOpen: boolean,
   *   failureCount: number,
   *   lastFailureTime: number|null,
   *   resetTimer: ReturnType<typeof setTimeout>|null
   * }>}
   */
  const state = new Map();
  /** @type {Set<() => void>} */
  const listeners = new Set();

  function emit() {
    listeners.forEach(l => l());
  }

  function entry(key) {
    let stateEntry = state.get(key);
    if (!stateEntry) {
      stateEntry = { isOpen: false, failureCount: 0, lastFailureTime: null, resetTimer: null };
      state.set(key, stateEntry);
    }
    return stateEntry;
  }

  /**
   * Half-open transition: close the breaker to let traffic through again, and
   * decrement (not zero) the failure count so a single probe failure doesn't
   * instantly re-trip at the same threshold — the count only fully clears on a
   * real success (recordSuccess), giving a recovering backend room to prove out.
   */
  function performAutoReset(stateEntry) {
    stateEntry.isOpen = false;
    stateEntry.failureCount = Math.max(0, stateEntry.failureCount - 1);
    stateEntry.resetTimer = null;
    emit();
  }

  /** Schedule a half-open auto-reset after `backoffMs`. */
  function scheduleAutoReset(stateEntry, backoffMs) {
    if (stateEntry.resetTimer) clearTimeout(stateEntry.resetTimer);
    stateEntry.resetTimer = setTimeout(() => performAutoReset(stateEntry), backoffMs);
  }

  /** @param {string} key */
  function isOpen(key) {
    return entry(key).isOpen;
  }

  /**
   * Record a failure for `key`; may open the breaker.
   * @param {string} key
   * @param {Error} error
   * @param {import('./errorClassification.js').ErrorClassification} [classification]
   *   Pre-computed classification (avoids re-classifying on hot error paths).
   */
  function recordFailure(key, error, classification = classifyError(error)) {
    const stateEntry = entry(key);

    stateEntry.failureCount += 1;
    stateEntry.lastFailureTime = Date.now();

    const shouldOpen =
      classification.type === 'auth' ||
      classification.type === 'rateLimit' ||
      (classification.type === 'gateway' && stateEntry.failureCount >= 2) ||
      stateEntry.failureCount >= 3;

    if (shouldOpen && !stateEntry.isOpen) {
      stateEntry.isOpen = true;

      // Half-open auto-reset for any non-auth trip (see module header).
      if (classification.type !== 'auth') {
        const backoffMs = Math.min(AUTO_RESET_BASE_MS * Math.pow(2, stateEntry.failureCount - 1), AUTO_RESET_CAP_MS);
        scheduleAutoReset(stateEntry, backoffMs);
      }
      emit();
    }
  }

  /**
   * Record a success for `key`; resets the breaker if it was tripped.
   * @param {string} key
   */
  function recordSuccess(key) {
    const stateEntry = entry(key);
    if (stateEntry.isOpen || stateEntry.failureCount > 0) {
      stateEntry.isOpen = false;
      stateEntry.failureCount = 0;
      stateEntry.lastFailureTime = null;
      if (stateEntry.resetTimer) {
        clearTimeout(stateEntry.resetTimer);
        stateEntry.resetTimer = null;
      }
      emit();
    }
  }

  /**
   * Subscribe to breaker state changes (for useSyncExternalStore).
   * @param {() => void} listener
   * @returns {() => void} unsubscribe
   */
  function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  /** Test/teardown helper: clear all state and timers. */
  function reset() {
    state.forEach(e => e.resetTimer && clearTimeout(e.resetTimer));
    state.clear();
    emit();
  }

  return { isOpen, recordFailure, recordSuccess, subscribe, reset };
}

/** App-wide singleton breaker shared by the query client. */
export const circuitBreaker = createCircuitBreaker();
